"use client";

import { db } from "@utils/IndexedDb";
import cn from "@utils/cn";
import { ProjectPage } from "@utils/damac/api";
import { getQuarter } from "@utils/dateUtils";
import { showOnlineExclusiveFeatureFlag } from "@utils/default";
import { currencyFormatter } from "@utils/formatedPrice";
import { slugify } from "@utils/string";
import { formatProjectSlug } from "@utils/textUtils";
import { deleteCookie, setCookie } from "cookies-next";
import { useSession } from "next-auth/react";
import { useFormatter, useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import SwiperCore from "swiper";
// import "swiper/css";
// import "swiper/css/navigation";
// import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import AddToFavorites from "@/components/AddToFavorites/AddToFavorites";
import { getAvailability } from "@/components/AvailableUnitsList/action";
import GalleryModal from "@/components/GalleryModal/GalleryModal";
import { useNotification } from "@/components/NotificationProvider/NotificationProvider";
import { NotificationType } from "@/components/NotificationProvider/type";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import CurrencyIcon from "@/components/ui/CurrencyIcon/CurrencyIcon";
import Icon from "@/components/ui/Icon/Icon";
import { projectlocale } from "@/constants/locale";
import { AvailableUnitContext } from "@/context/AvailableUnitListContext";
import { LoadingContext } from "@/context/LoaderContext";
import { ModalsContext } from "@/context/ModalsContext";
import { SelectUnitContext } from "@/context/SelectUnitContext";
import { UTMParametersContext } from "@/context/UTMParametersContext";
import { UserContext } from "@/context/UserContext";
import { brochureDowloadCtaClick, pushMetaversCtaClick } from "@/dataLayer/dataLayer";
import { bookVisitTracking, ecomInitiateCheckout, ecomSelectReserve, entryPointCtaClick } from "@/dataLayer/dataLayer";
import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import useMediaQuery from "@/hooks/useMediaQuery";
import useResponsiveLabel from "@/hooks/useResponsiveLabel";
import { CommercetoolsUnit, Media } from "@/types";

import { MicrocopyComponent } from "../AgentChatBot/MicroComponent";
import CardTags from "../AutogeneratedCarousel/CardTags";
import ButtonOrVideoModal from "../ui/ButtonOrVideoModal/ButtonOrVideoModal";
import Modal from "../ui/Modal/Modal";
import ModalContent from "../ui/Modal/ModalContent";
import "./UnitPageTemplate.css";

SwiperCore.use([Pagination, Navigation]);

interface UnitGalleryProps {
  entry: Partial<ProjectPage>;
  hasOtherSolutions?: boolean;
  params?: any;
  serverSideCookieCurrency?: string;
  currencyFromApiRes?: string;
  isBelongToCommunity?: any;
  matchedCommunity?: any;
  projectShowOnWebsite?: any;
  projectPDF?: string;
  videocontent?: any;
  projectData?: any;
  leadFormPageLocation?: string;
  microcopyComponents?: any[];
  isPropertyUnitPage?: boolean;
}

// @TODO: recive mime type from BE
function isVideo(url: string) {
  const videoExtensions = [".mp4", ".avi", ".mov", ".wmv", ".flv", ".mkv", ".webm", ".mpeg", ".mpg", ".3gp", ".m4v"];
  url = url.toLowerCase();
  return videoExtensions.some((ext) => url.endsWith(ext));
}

export function getStructuredDataFromUnit(unit: CommercetoolsUnit) {
  return {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: unit.name,
    image: unit.images || [],
    description: unit.project,
    offers: {
      "@type": "Offer",
      price: unit.price,
      priceCurrency: unit.currency,
    },
  };
}

const UnitGallery: FC<UnitGalleryProps> = ({
  entry,
  hasOtherSolutions,
  params,
  serverSideCookieCurrency,
  currencyFromApiRes,
  isBelongToCommunity,
  matchedCommunity,
  projectShowOnWebsite,
  projectPDF,
  videocontent,
  projectData,
  leadFormPageLocation,
  microcopyComponents,
}) => {
  const { data: session } = useSession();
  const [isHovered, setIsHovered] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isSo = useIsSaleScreen();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [unitNotAvailable, setUnitNotAvailable] = useState<boolean>(false);
  const [unitAvailableLoading, setUnitAvailableLoading] = useState<boolean>(false);
  const [isUnitAvailabledChecked, setIsUnitAvailabledChecked] = useState<boolean>(false);
  const [isPropertyTypePage, setIsPropertyTypePage] = useState<boolean>(true);
  const [tourModalOpen, setTourModalOpen] = useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const { currency, measurement } = useContext(UserContext);
  const { updateUnitTitle } = useContext(SelectUnitContext);
  const { utmParameters } = useContext(UTMParametersContext);
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [isLoadingForPrice, setIsLoadingForPrice] = useState(true);
  const [language] = locale.split("-");
  const slug = entry.slug;
  const segments = pathname.split("/").filter(Boolean);
  const togglePlay = () => {
    if (videoRef.current?.paused) {
      videoRef.current?.play();
      setIsPlaying(true);
    } else {
      videoRef.current?.pause();
      setIsPlaying(false);
    }
  };

  const {
    setBookVisitModalOpen,
    setEnquireModalOpen,
    setAdditionalInfo,
    setGetFormPageLocation,
    setGetInTouchModalOpen,
    additionalInfo,
    settings,
    setDownloadBrochureModalOpen,
  } = useContext(ModalsContext);
  const { filteredResults, filterResultLoading, total } = useContext(AvailableUnitContext);
  const [unit, setUnit] = useState<CommercetoolsUnit | undefined>(undefined);
  const [translatedProjectName, setTranslatedProjectName] = useState("");
  const [count, setCount] = useState<number>(0);
  const showPrice = !!entry?.projectData && !entry.projectData.error && !(!isPropertyTypePage && unitNotAvailable);
  const propertyType = entry.projectData?.propertyType?.[0] ?? "";
  const bedrooms = Array.isArray(entry.projectData?.bedrooms) ? entry.projectData.bedrooms[0] : entry.projectData?.bedrooms ?? "";
  const propertyTypeTitle =
    propertyType === ""
      ? entry?.slug?.replace("-", " ") || ""
      : locale?.split("-")?.[0] === "ru"
        ? propertyType === "Apartment" || propertyType === "apartment" || propertyType === "APARTMENT"
          ? t("label.propertyType.ruName", { count: `${bedrooms}` })
          : t(`label.propertyType.type.${propertyType ?? ""}`) +
            " " +
            t("label.propertyType.brName", { bedrooms: `${bedrooms}`, count: `${bedrooms}` })
        : locale?.split("-")?.[0] === "zh"
          ? t("label.propertyType.brName", { count: `${bedrooms}` }) + t(`label.propertyType.type.${propertyType ?? ""}`)
          : locale?.split("-")?.[0] === "tr"
            ? t("label.propertyType.brName", { bedrooms: `${bedrooms}` }) + " " + t(`label.propertyType.type.${propertyType ?? ""}`)
            : locale?.split("-")?.[0] === "de"
              ? t(`label.propertyType.type.${propertyType ?? ""}`) +
                " " +
                t("label.common.of") +
                " " +
                t("label.propertyType.brName", { bedrooms: `${bedrooms}`, count: `${bedrooms}` })
              : bedrooms && bedrooms !== "0"
                ? t(`label.propertyType.type.${propertyType}`) + " " + t("label.propertyType.brName", { bedrooms: bedrooms, count: bedrooms })
                : t("label.propertyType.studio") + " " + t(`label.propertyType.type.${propertyType}`);
  const unitTitle = `${bedrooms && bedrooms !== "0" ? t("label.unit.brName", { bedrooms }) : t("label.propertyType.studio")} ${t(`label.unit.type.${propertyType}`)}`;
  useEffect(() => {
    const splitPath = pathname.split("/");
    const last = splitPath[splitPath.length - 2] || "";
    const unitCodeLike = /^[a-z0-9]+-\d+-[a-z0-9]+$/i.test(last);
    !unitCodeLike ? setIsPropertyTypePage(true) : setIsPropertyTypePage(false);

    return () => {
      setUnitNotAvailable(false);
    };
  }, []);

  useEffect(() => {
    updateUnitTitle(unit ? unitTitle : propertyTypeTitle);
  }, [unit, updateUnitTitle, unitTitle, propertyTypeTitle]);

  useEffect(() => {
    filteredResults?.length && loading(false);
    filteredResults?.length && setUnit(filteredResults.length === 1 ? filteredResults[0] : undefined);
    if (filteredResults.length === 1) {
      setUnitAvailableLoading(true);
      isUnitAvailableForReservation(filteredResults[0].inventoryID, filteredResults[0].id).then((isAvailable) => {
        setUnitNotAvailable(!isAvailable);
        setUnitAvailableLoading(false);
        setIsUnitAvailabledChecked(true);
      });
    }
    if (!filteredResults?.length && hasLoaded) {
      const splitPath = pathname.split("/");
      const last = splitPath[splitPath.length - 2] || "";
      const unitCodeLike = /^dcs-\d+-\d+$/i.test(last);
      !unitCodeLike && setIsPropertyTypePage(true);
      setUnitNotAvailable(true);
    }
    if (filteredResults?.length && hasLoaded) {
      setIsPropertyTypePage(false);
    }
  }, [filteredResults]);

  const enquireLabel = useResponsiveLabel(t("stickybar.mobile.enquire"), t("stickybar.desktop.enquire"));
  const { loading } = useContext(LoadingContext);
  const { showNotification } = useNotification();
  const { showUnavailableUnit } = useNotification();

  const sessionData = typeof window !== "undefined" ? window.sessionStorage.getItem("propertitypeCard") : null;

  const propertitypeCard = sessionData ? JSON.parse(sessionData) : {};

  const tags = (() => {
    const result = [];
    if (entry?.projectData && entry?.projectData?.error) {
      return [];
    }
    if (projectData?.exclusive && showOnlineExclusiveFeatureFlag) {
      result.push({ label: t("filter.label.onlineExclusive"), isHiglighted: false });
    }
    if (projectData?.goldenVisa) {
      result.push({ label: t("filter.label.others.goldenVisaText"), isHiglighted: true });
    }

    return result;
  })();

  const showTags = Array.isArray(tags) && tags.length > 0 && (!params?.isPropertyUnitPage || (isUnitAvailabledChecked && !unitNotAvailable));

  // --- Project name translation
  const getTranslatedData = async (id: string, lang: string | "", data: any) => {
    const table = db.PROJECTS;
    const rawValues = await table.toArray();
    const product = rawValues.filter((item) => item.assignedCategory === id);
    const translation = projectlocale.find((item) => item.id === product[0].id);
    if (translation) {
      return translation?.name[lang as keyof typeof translation.name] || data?.name;
    }
  };

  // const tags = typeof slug === "string" && propertitypeCard[slug] === true ? [{ label: "Golden Visa", isHiglighted: true }] : [];

  const isUnitAvailableForReservation = async (inventoryID: string, id: string) => {
    return await getAvailability(inventoryID, id);
  };

  // TODO: use one parameter only
  const handleReserve = async (unit: CommercetoolsUnit, inventoryID: string, id: string) => {
    loading(true);
    const response = await getAvailability(inventoryID, id);
    if (response) {
      ecomInitiateCheckout({
        cta_name: t("unit.reserve.now"),
        property_name: unit.project,
        project_location: `${unit.area}, ${unit.city}`,
        property_type: unit.identifier,
        bedroom_numbers: unit.bedrooms,
        bathrooms: "no of bathrooms given", // TODO fixme when data will come
        unit_id: unit.name,
        unit_type: unit.unitType,
        unit_category: unit.isFurnished ? "furnished" : "unfurnished",
        unit_size: unit.areaSqft,
        unit_price: unit.price,
        currency: unit.currency,
        handover_qaurter_year: getQuarter(unit.deliveryDate, locale, t),
        status: unit.status,
        user_logged_in: !!session,
        form_name: "online_reservation",
        // user_id: session?.user.id || "",
      });
      // router.push(`/${locale}/checkout`, { scroll: true });
      router.push(`/${locale}/checkout?step=contact`, { scroll: true });
    } else {
      loading(false);
      showUnavailableUnit(
        filteredResults.length <= 1
          ? isBelongToCommunity
            ? `/${locale}/communities/${matchedCommunity.slug}/projects/${entry.projectSlug}/`
            : `/${locale}/projects/${entry.projectSlug}/`
          : undefined,
      );
      // showNotification(t("reservation.label.unitUnavailable"), 3000, NotificationType.ERROR);
    }
  };
  const setSessionData = (data: Record<string, string>) => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("stickyBarData", JSON.stringify(data));
    }
  };

  const handleClick = () => {
    loading(true);
    const splitPath = pathname.split("/");
    const last = splitPath[splitPath.length - 2] || "";
    const unitCodeLike = /^[a-z0-9]+-\d+-[a-z0-9]+$/i.test(last);
    const newPath = unitCodeLike ? pathname.split("/").slice(0, -2).join("/") : pathname.split("/").slice(0, -3).join("/");
    !unitCodeLike && localStorage.setItem("redirected", "true");
    const projectSlug = newPath.split("/").filter(Boolean).pop();
    projectSlug && localStorage.setItem("redirectedProject", projectSlug);
    router.push(newPath);
    // router.refresh();
  };

  const format = useFormatter();
  const formattedPrice = currencyFormatter(unit ? Number(unit.price) : Number(entry.projectData?.priceMin), currency, format);

  let structuredData = params.unitName && unit && getStructuredDataFromUnit(unit);

  const share = async (shareData: any) => {
    const unitName = unit?.name.replaceAll("/", "-");
    const projectName = unit?.project ?? params?.project ?? null;
    const bedroomsIdentifier = unit?.bedrooms && unit?.identifier ? unit?.bedrooms + " " + unit?.identifier : params?.propertyType ?? null;
    let shareUrl;
    if (projectName) {
      const name = unitName && filteredResults.length === 1 ? unitName + "/" : "";
      // shareUrl = `/${locale}/projects/${slugify(projectName)}/properties/${slugify(bedroomsIdentifier)}/${name}`;
      shareUrl =
        isBelongToCommunity && matchedCommunity
          ? `/${locale}/communities/${matchedCommunity.slug}/projects/${slugify(projectName)}/properties/${slugify(bedroomsIdentifier)}/${name}`
          : `/${locale}/projects/${slugify(projectName)}/properties/${slugify(bedroomsIdentifier)}/${name}`;
    }

    if (navigator.share) {
      shareData.url = shareUrl;
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error(err);
      }
    } else {
      navigator.clipboard.writeText(shareData.url);
      alert(t("copy.link"));
      console.log("share not supported");
    }
  };

  if (unit) {
    const path = location.pathname.replace(/\/$/, "").toLowerCase();
    const suffix = `/${unit.name.replaceAll("/", "-").toLowerCase()}`;
    if (!path.endsWith(suffix)) {
      // https://nextjs.org/docs/app/building-your-application/routing/linking-and-navigating#windowhistoryreplacestate
      window.history.replaceState(null, "", `${path}${suffix}/`);
    }
  }
  useEffect(() => {
    if (language !== "en" && entry?.projectData?.projectID) {
      getTranslatedData(entry.projectData.projectID, language, entry.projectData).then((name) => {
        if (name) setTranslatedProjectName(name);
      });
    } else {
      setTranslatedProjectName(entry?.projectData?.projectName || formatProjectSlug(entry?.projectSlug || "") || "");
    }
  }, [language, entry?.projectData?.projectID]);

  useEffect(() => {
    setAdditionalInfo((prev: Record<string, any>) => ({
      ...prev,
      project: entry.projectData?.project,
      project_status: entry.projectData?.status,
      inventoryID: unit?.inventoryID,
    }));
  }, [setAdditionalInfo, entry?.projectData, unit?.inventoryID]);

  useEffect(() => {
    if (serverSideCookieCurrency && currencyFromApiRes && currency) {
      if (serverSideCookieCurrency === currencyFromApiRes && currencyFromApiRes === currency) {
        setIsLoadingForPrice(false);
      } else {
        setIsLoadingForPrice(true);
      }
    }
  }, [serverSideCookieCurrency, currencyFromApiRes, currency]);
  useEffect(() => {
    // Set hasLoaded to true after the first API response
    if (!filterResultLoading) {
      setHasLoaded(true);
    }
  }, [filterResultLoading]);
  useEffect(() => {
    // Only run after loading is finished and we have a real API response
    if (
      typeof window !== "undefined" &&
      !filterResultLoading &&
      utmParameters?.landingPageURL === window.location.href &&
      total === 0 &&
      hasLoaded &&
      Array.isArray(filteredResults) &&
      filteredResults.length === 0 &&
      !unit
    ) {
      // const path = window.location.pathname;
      // const match = path.match(/(.*\/properties\/[^\/]+)/);
      // if (match && match[1] && path !== match[1]) {
      //   window.history.replaceState(null, "", match[1]);
      // }
      setUnitNotAvailable(true);
    }
  }, [filterResultLoading, total, filteredResults, utmParameters, hasLoaded, unit]);

  let finalUrlForCommunity =
    isBelongToCommunity && matchedCommunity
      ? `/${locale}/communities/${matchedCommunity.slug}/projects/${entry.projectSlug}/`
      : `/${locale}/projects/${entry.projectSlug}/`;

  const payExpressionOfInterestLabel = useResponsiveLabel(
    t("stickybar.mobile.payExpressionOfInterest"),
    t("stickybar.desktop.payExpressionOfInterest"),
  );

  const renderUnitNotAvailable = () => {
    return (
      <div className="flex justify-center items-center bg-gray-10 pt-8">
        <span className="text-[12px] font-secondary font-medium p-[10px]">
          {t("title.label.unitNotAvailable")}
          <Button
            className="text-[12px] font-secondary font-medium pl-[8px] underline"
            variant={ButtonVariant.TERTIARY}
            size={ButtonSize.SMALL}
            theme={ButtonTheme.BLACK}
            onClick={handleClick}
          >
            {t("button.label.otherUnit")}
          </Button>
        </span>
      </div>
    );
  };

  return (
    <div id={`unit-gallery-${entry.slug}`}>
      {isDesktop && unitNotAvailable && renderUnitNotAvailable()}
      {entry?.media?.[0] && (
        <Image
          className="w-full h-[375px] md:h-none object-cover md:hidden "
          src={entry.media[0].url}
          alt={entry.media[0].title}
          width={entry.media[0].width}
          height={entry.media[0].height}
        />
      )}
      <div className="container-fixed-carousel w-full md:h-dvh relative bg-white flex flex-col md:!flex-row pb-10">
        <div className="md:w-16\/24 h-full hidden md:block unit-carousel relative">
          <Swiper
            speed={1000}
            className="w-full h-full z-10"
            slidesPerView={1}
            direction="horizontal"
            pagination={{ type: "bullets", clickable: false }}
            navigation={{
              nextEl: `.unit-gallery-next-${entry.slug}`,
              prevEl: `.unit-gallery-prev-${entry.slug}`,
            }}
            onTransitionStart={(swiper) => {
              if (!(swiper as SwiperCore).isEnd) {
                document.body.scrollTop = 0;
                document.documentElement.scrollTop = 0;
              }
            }}
          >
            {entry.media?.slice(0, 4).map((media: Media, index: number) => (
              <SwiperSlide key={index} className="relative">
                <div className="img-overlay-1 absolute top-0 left-0 h-full w-full"></div>
                {isVideo(media.url) ? (
                  <>
                    <video className="object-cover w-full h-full" muted loop autoPlay playsInline ref={videoRef}>
                      <source src={media.url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    <Button
                      size={ButtonSize.SMALL}
                      customClass="md:flex items-center border-none w-fit h-fit p-xs font-medium box-material-primary backdrop-blur-xl overflow-hidden absolute bottom-[120px] right-l rounded-3xl"
                      onClick={togglePlay}
                    >
                      <Icon name={isPlaying ? "pause" : "play"} size="24" title="Pause" className="stroke-white" />
                    </Button>
                  </>
                ) : (
                  <Image className="object-cover w-full h-full " src={media.url} alt={media.title} height={media.height} width={media.width} />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
          <button
            type="button"
            aria-label="Previous slide"
            className={`unit-gallery-nav unit-gallery-prev-${entry.slug} ${locale?.split("-")?.[0] === "ar" ? "right-l" : "left-l"}`}
          >
            <Icon name="arrow-left" size="20" title="Previous slide" className="stroke-white" />
          </button>
          <button
            type="button"
            aria-label="Next slide"
            className={`unit-gallery-nav unit-gallery-next-${entry.slug} ${locale?.split("-")?.[0] === "ar" ? "left-l" : "right-l"}`}
          >
            <Icon name="arrow-right" size="20" title="Next slide" className="stroke-white" />
          </button>
          <div className={`absolute p-l ${locale?.split("-")?.[0] === "ar" ? "right-0" : "left-0"} top-0 z-20 flex`}>
            {entry.media && entry.media?.length > 4 && (
              <>
                <Button
                  size={ButtonSize.SMALL}
                  customClass="stroke-white !border-none text-white btn-glass p-xs mr-m"
                  theme={ButtonTheme.WHITE}
                  type="button"
                  variant={ButtonVariant.PRIMARY}
                  onClick={() => setModalOpen(true)}
                >
                  <span className="flex items-center gap-xs normal-case text-white">
                    <Icon name="image" size="20" className="stroke-white" />
                    {t("button.label.allimages")}
                  </span>
                </Button>
              </>
            )}
            <Button
              size={ButtonSize.SMALL}
              customClass={`p-xs stroke-white !border-none btn-glass ${locale?.split("-")?.[0] === "ar" ? "mr-m" : ""}`}
              theme={ButtonTheme.WHITE}
              type="button"
              variant={ButtonVariant.PRIMARY}
              onClick={() => {
                window.location.href = "#AUL-component";
              }}
            >
              <span className="flex items-center normal-case gap-xs  text-white">
                <Icon name="plan" size="20" className="stroke-white" />
                {t("button.label.floorplan")}
              </span>
            </Button>
          </div>
        </div>
        {!isDesktop && unitNotAvailable && renderUnitNotAvailable()}
        <div className="md:w-8\/24 w-full  h-full flex md:items-start md:mt-xxl">
          <div className="mt-m md:mt-none w-22\/24 md:w-20\/24 mx-auto">
            <div className="flex">
              <div className="w-2/3 flex items-center gap-xxs">
                {entry.communitySlug ? (
                  <>
                    <span className="font-normal font-secondary text-s flex items-center">
                      <a href={`/${locale}/communities/${entry.communitySlug}/`} className="underline">
                        {entry.projectData?.location}
                      </a>
                    </span>
                    <span>&gt;</span>
                  </>
                ) : (
                  unitNotAvailable &&
                  segments[1] === "communities" && (
                    <>
                      <span className="font-normal font-secondary text-s flex items-center">
                        <a href={`/${locale}/communities/${segments[2]}/`} className="underline">
                          {formatProjectSlug(segments[2])}
                        </a>
                      </span>
                      <span>&gt;</span>
                    </>
                  )
                )}
                <span className="font-normal font-secondary text-s flex items-center">
                  <a href={finalUrlForCommunity} className="underline">
                    {translatedProjectName}
                  </a>
                </span>
                {/* {unit && ( */}
                {hasOtherSolutions && (
                  <>
                    <span>&gt;</span>
                    <span className="font-normal font-secondary text-s flex items-center">
                      <a href={`${finalUrlForCommunity}properties/${entry.slug}/`} className="underline">
                        {propertyTypeTitle}
                      </a>
                    </span>
                  </>
                )}
                <span className="underlined-row"></span>
              </div>
              <div className="w-1/3 text-right">
                <div className="flex z-20 ml-auto items-center justify-end">
                  {/* <button onClick={() => share({ title: unit.name, text: unit.name, url: window.location.href })}> */}
                  <button
                    onClick={() => share({ title: t("title.common.shareUnit"), text: entry.internalName, url: "" })}
                    className="flex"
                    style={{ zIndex: 50 }}
                  >
                    <Icon name="share" size={"20"} className="stroke-black mr-m" />
                  </button>
                  <AddToFavorites item={unit ?? entry.projectData} className="relative z-20 flex" fillColor="fill-black" />
                </div>
              </div>
            </div>
            {entry.projectData && currency && filterResultLoading ? (
              <div> {t("common.loading")}</div>
            ) : (
              <>
                <h1 className="font-primary font-medium text-m uppercase leading-5 tracking-widest my-m md:mt-xl md:mb-[40px]">
                  {unit ? unitTitle : propertyTypeTitle}
                </h1>
                <div>
                  {(!params?.isPropertyUnitPage || isUnitAvailabledChecked) && entry?.projectData && !entry.projectData.error && (
                    <span className="font-normal font-secondary text-s leading-4">
                      {!params?.isPropertyUnitPage && !filterResultLoading
                        ? unit
                          ? unit.name + " "
                          : params?.isPropertyUnitPage
                            ? ""
                            : Number(entry?.projectData?.priceMax) === Number(entry?.projectData?.priceMin)
                              ? ""
                              : t("label.common.from") + " "
                        : ""}
                      {params?.isPropertyUnitPage && !filterResultLoading ? (
                        // Always run this formatting on Property Unit Page (ignore `unit`)
                        typeof formattedPrice === "string" && formattedPrice.includes("AED") ? (
                          <>
                            {currency === "AED" && <CurrencyIcon color="black" />} {formattedPrice.replace("AED", "").trim()}
                          </>
                        ) : locale?.split("-")?.[0] === "ar" ? (
                          <>
                            {formattedPrice.replace("AED", "").trim()}
                            {currency === "AED" && <CurrencyIcon color="black" />}
                          </>
                        ) : (
                          formattedPrice
                        )
                      ) : // Non-Property Unit Page: keep the old dependency on `unit`
                      unit && typeof formattedPrice === "string" && formattedPrice.includes("AED") ? (
                        <>
                          {currency === "AED" && <CurrencyIcon color="black" />} {formattedPrice.replace("AED", "").trim()}
                        </>
                      ) : locale?.split("-")?.[0] === "ar" ? (
                        <>
                          {formattedPrice.replace(/AED|د\.إ\./g, "").trim()}
                          {currency === "AED" && <CurrencyIcon color="black" />}
                        </>
                      ) : formattedPrice.includes("AED") ? (
                        <>
                          {currency === "AED" && <CurrencyIcon color="black" />}
                          {formattedPrice.replace("AED", "").trim()}
                        </>
                      ) : (
                        formattedPrice
                      )}

                      {/* {isPropertyUnitPage && unit && typeof formattedPrice === "string" && formattedPrice.includes("AED") ? (
                      <>
                        <CurrencyIcon color="black" /> {formattedPrice.replace("AED", "").trim()}
                      </>
                    ) : locale?.split("-")?.[0] === "ar" ? (
                      <>
                        {formattedPrice.replace(/AED|د\.إ\./g, "").trim()}
                        <CurrencyIcon color="black" />
                      </>
                    ) : (
                      formattedPrice
                    )} */}
                    </span>
                  )}
                </div>
              </>
            )}
            <div className={cn("md:mt-xxl md:mb-3xl ", entry.others && entry.others.length >= 1 && "my-l grid grid-cols-3 gap-xxs")}>
              {entry.others?.map((tag: string, index: number) => (
                <div key={index} className="uppercase text-center bg-gray-7 rounded-md">
                  <span className="font-normal font-secondary text-s">{tag}</span>
                </div>
              ))}
            </div>
            {/* @TODO: Partner Logo da dove li prendiamo?*/}
            <div className="flex flex-col-reverse md:flex-col mb-l md:mb-0">
              {/* <div className="flex mb-l md:mb-xxl mt-l md:mt-0">
                <div className="flex items-center w-1/2 text-left">
                  <span className="text-xs font-normal font-secondary">{data.partner.labelLogo}</span>
                </div>
                <div className="flex justify-end w-1/2 text-right">
                  <Image src={data.partner.imgLogo} alt={data.partner.labelLogo} height="56" width="69" />
                </div>
              </div> */}
              <div className="flex flex-col w-full uppercase mt-l">
                {showTags && tags && tags.length > 0 && (
                  <CardTags tags={tags} isPostionAbsolute={false} className="mb-[20px]" spanClassName="flex-1 text-center" />
                )}
                {isSo ? (
                  unit?.virtualTourUrl ? (
                    <Button
                      size={ButtonSize.MEDIUM}
                      customClass="mb-m"
                      theme={ButtonTheme.WHITE}
                      type="button"
                      variant={ButtonVariant.PRIMARY}
                      onClick={() => {
                        setTourModalOpen(true);

                        pushMetaversCtaClick(
                          t("button.label.3dexplore"),
                          projectData?.projectName ?? "",
                          projectData?.city ?? "",
                          projectData?.propertyType[0] ?? "",
                          entry?.slug ?? "",
                        );
                      }}
                    >
                      {t("button.label.3dexplore")}
                    </Button>
                  ) : (
                    !projectShowOnWebsite && (
                      <Button
                        size={ButtonSize.MEDIUM}
                        theme={ButtonTheme.BLACK}
                        type="button"
                        variant={ButtonVariant.PRIMARY}
                        onClick={() => {
                          if (entry.projectData) {
                            const projectName = entry?.projectData?.projectName.replaceAll(" ", "-").toLowerCase();
                            const productIdentifier = `${projectName ?? ""}/${entry?.projectData?.slug ?? ""}`;
                            ecomSelectReserve({
                              cta_name: t("button.label.reserve"),
                              property_name: entry.projectData.projectName,
                              form_name: "online_reservation",
                              project_location: `${entry.projectData.area}, ${entry.projectData.location}`,
                              property_type: entry.projectData.propertyType[0],
                              bedroom_numbers: Array.isArray(entry.projectData?.bedrooms)
                                ? entry.projectData.bedrooms[0]
                                : entry.projectData?.bedrooms ?? "",
                              bathrooms: "no of bathrooms given", // TODO fixme when data will come
                              // unit_id: unit.name,
                              product_identifier: productIdentifier,
                              // unit_type: unit.unitType,
                              unit_category: entry.projectData.isFurnished ? "furnished" : "unfurnished",
                              unit_size: entry.projectData.areaMax,
                              unit_price: Math.round(Number(entry.projectData.priceMin) / 100),
                              currency: entry.projectData.currency,
                              handover_qaurter_year: getQuarter(entry.projectData.deliveryDate, locale, t),
                              status: entry.projectData.status || "",
                              user_logged_in: !!session,
                              // user_id: session?.user.id || "",
                            });
                          }
                          const AULComponent = document.body.querySelector("#AUL-component");
                          if (AULComponent) {
                            const rect = AULComponent.getBoundingClientRect();
                            window.scrollTo({
                              top: rect.top + window.scrollY,
                              behavior: "smooth",
                            });
                          }
                        }}
                      >
                        {t("button.label.reserve")}
                      </Button>
                    )
                  )
                ) : (
                  <>
                    {filteredResults?.length > 0 && unit?.virtualTourUrl ? (
                      <Button
                        size={ButtonSize.MEDIUM}
                        customClass="mb-m"
                        theme={ButtonTheme.WHITE}
                        type="button"
                        variant={ButtonVariant.PRIMARY}
                        onClick={() => {
                          setTourModalOpen(true);

                          pushMetaversCtaClick(
                            t("button.label.3dexplore"),
                            projectData?.projectName ?? "",
                            projectData?.city ?? "",
                            projectData?.propertyType[0] ?? "",
                            entry?.slug ?? "",
                          );
                        }}
                      >
                        {t("button.label.3dexplore")}
                      </Button>
                    ) : (
                      <Button
                        size={ButtonSize.MEDIUM}
                        customClass="mb-m"
                        theme={ButtonTheme.WHITE}
                        type="button"
                        variant={ButtonVariant.PRIMARY}
                        onClick={() => {
                          // BUG 93898
                          const unit0 = unit ?? filteredResults?.[0];
                          // console.error("unit0", unit0);
                          bookVisitTracking({
                            cta_name: t("button.label.3dtour"),
                            project_name: unit0?.project ?? "",
                            offer_tag_label: unit0?.tags ?? [],
                            property_indicative_price: Math.round(unit ? Number(unit.price) : Number(entry.projectData?.priceMin) / 100 || "" || 0),
                            unit_number: unit0?.name ?? "",
                            project_city: unit0?.city ?? "",
                            project_type: unit0?.identifier ?? "",
                            bedrooms_number: unit0?.bedrooms ? `${unit0?.bedrooms}` : "",
                            title_text: unit0?.propertyName ?? "",
                          });
                          if (entry) {
                            setSessionData({ projectName: entry?.projectData?.projectName ?? "not_available" });
                          }
                          unitNotAvailable ? setGetInTouchModalOpen(true) : setBookVisitModalOpen(true);
                          setGetFormPageLocation(leadFormPageLocation);
                        }}
                      >
                        {unitNotAvailable ? t("get-in-touch") : t("button.label.3dtour")}
                      </Button>
                    )}
                  </>
                )}

                {unit?.virtualTourUrl && (
                  <Modal
                    open={tourModalOpen}
                    onClose={() => {
                      setTourModalOpen(false);
                    }}
                  >
                    <ModalContent className="w-full h-full !rounded-none  py-l">
                      <div className="flex justify-between pb-l">
                        <h2 className="font-medium uppercase font-primary text-m ">{t("unit.3dtourmodal")}</h2>
                        <Button
                          className="modal__close"
                          size={ButtonSize.MEDIUM}
                          variant={ButtonVariant.TERTIARY}
                          theme={ButtonTheme.BLACK}
                          onClick={() => setTourModalOpen(false)}
                        >
                          <span className="pointer-events-none">
                            <Icon name="close" size="24" title="close" className="stroke-black" />
                            <span className="sr-only">{t("button.label.close")}</span>
                          </span>
                        </Button>
                      </div>
                      <iframe
                        className="object-cover w-full h-[calc(100dvh-100px)] rounded-lg border-0"
                        width="560"
                        height="315"
                        src={unit?.virtualTourUrl}
                        title="3d Tour"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                      ></iframe>
                    </ModalContent>
                  </Modal>
                )}

                {!isSo &&
                  !isPropertyTypePage &&
                  (!filterResultLoading ? (
                    unit ? (
                      <Button
                        size={ButtonSize.MEDIUM}
                        theme={ButtonTheme.BLACK}
                        type="button"
                        variant={ButtonVariant.PRIMARY}
                        onClick={() => {
                          if (unit.sellable) {
                            // TODO: use one parameter only
                            handleReserve(unit, unit.inventoryID, unit.id);
                          } else {
                            setEnquireModalOpen(true);
                            setGetFormPageLocation(leadFormPageLocation);
                          }
                        }}
                      >
                        {unit.sellable ? t("unit.reserve.now") : enquireLabel}
                      </Button>
                    ) : projectShowOnWebsite ? (
                      <Button
                        size={ButtonSize.MEDIUM}
                        theme={ButtonTheme.BLACK}
                        type="button"
                        variant={ButtonVariant.PRIMARY}
                        onClick={() => {
                          if (entry?.projectData?.projectName) {
                            setSessionData({ projectName: entry?.projectData?.projectName ?? "not_available" });
                            sessionStorage.setItem("propertyTypeData", JSON.stringify(projectData));
                            loading(true);
                            setCookie("eoiID", entry?.projectData?.id);
                            // entryPointCtaClick({ cta_name: t("stickybar.mobile.enquire"), ...additionalInfo });
                            ecomInitiateCheckout({
                              cta_name: "PAY EXPRESSION OF INTEREST",
                              form_name: "prebook_online",
                              ...additionalInfo,
                            });

                            deleteCookie("reserveID");
                            router.push(`/${locale}/checkout?step=contact`, { scroll: true });
                          }
                        }}
                      >
                        {payExpressionOfInterestLabel}
                      </Button>
                    ) : unitNotAvailable ? (
                      ""
                    ) : (
                      <Button
                        size={ButtonSize.MEDIUM}
                        theme={ButtonTheme.BLACK}
                        type="button"
                        variant={ButtonVariant.PRIMARY}
                        onClick={() => {
                          if (unitNotAvailable) {
                            handleClick();
                            return;
                          }
                          if (entry.projectData) {
                            const projectName = entry?.projectData?.projectName.replaceAll(" ", "-").toLowerCase();
                            const productIdentifier = `${projectName ?? ""}/${entry?.projectData?.slug ?? ""}`;
                            ecomSelectReserve({
                              cta_name: t("button.label.reserve"),
                              property_name: entry.projectData.projectName,
                              form_name: "online_reservation",
                              project_location: `${entry.projectData.area}, ${entry.projectData.location}`,
                              property_type: entry.projectData.propertyType[0],
                              bedroom_numbers: Array.isArray(entry.projectData?.bedrooms)
                                ? entry.projectData.bedrooms[0]
                                : entry.projectData?.bedrooms ?? "",
                              bathrooms: "no of bathrooms given", // TODO fixme when data will come
                              // unit_id: unit.name,
                              product_identifier: productIdentifier,
                              // unit_type: unit.unitType,
                              unit_category: entry.projectData.isFurnished ? "furnished" : "unfurnished",
                              unit_size: entry.projectData.areaMax,
                              unit_price: Math.round(Number(entry.projectData.priceMin) / 100),
                              currency: entry.projectData.currency,
                              handover_qaurter_year: getQuarter(entry.projectData.deliveryDate, locale, t),
                              status: entry.projectData.status || "",
                              user_logged_in: !!session,
                              // user_id: session?.user.id || "",
                            });
                          }

                          const AULComponent = document.body.querySelector("#AUL-component");
                          if (AULComponent) {
                            const rect = AULComponent.getBoundingClientRect();
                            window.scrollTo({
                              top: rect.top + window.scrollY,
                              behavior: "smooth",
                            });
                          }
                        }}
                      >
                        {unitNotAvailable ? t("button.label.otherUnit") : t("button.label.reserve")}
                      </Button>
                    )
                  ) : !unitNotAvailable ? (
                    ""
                  ) : (
                    ""
                    // <Button size={ButtonSize.MEDIUM} customClass="mb-m" theme={ButtonTheme.WHITE} type="button" variant={ButtonVariant.PRIMARY}>
                    //   <div className="flex space-x-2">
                    //     <div className="h-5 w-5 bg-[#242121] rounded-full  text-[#242121]  animate-bounce [animation-delay:-0.3s]">...</div>
                    //     <div
                    //       className="h-5 w-5 bg-[#7F7777] rounded-full  text-[#7F7777]  animate-bounce [animation-delay:-0.15s]"
                    //       style={{ marginLeft: "1px" }}
                    //     >
                    //       ...
                    //     </div>
                    //     <div className="h-5 w-5 bg-[#A8A8A8] rounded-full  text-[#A8A8A8] animate-bounce" style={{ marginLeft: "1px" }}>
                    //       ...
                    //     </div>
                    //     <div className="h-5 w-5 bg-[#B7B7B7] rounded-full  text-[#B7B7B7]  animate-bounce" style={{ marginLeft: "1px" }}>
                    //       ...
                    //     </div>
                    //   </div>
                    // </Button>
                  ))}
              </div>
              {microcopyComponents &&
                microcopyComponents.length > 0 &&
                microcopyComponents?.map((microcopyComponent, index) => (
                  <React.Fragment key={index}>
                    <MicrocopyComponent {...microcopyComponent} />
                  </React.Fragment>
                ))}
            </div>
          </div>
        </div>

        <div className="w-22\/24 mx-auto md:hidden pb-l">
          <div className="flex flex-columns flex-wrap gap-xxs">
            {entry.media?.slice(0, 3).map((media, index) => (
              <div className={cn(index === 0 ? "w-full" : "w-[calc(50%-2px)]", index === 2 && "relative")} key={index}>
                {isVideo(media.url) ? (
                  <div
                    className={cn(
                      "w-full aspect-4-3 overflow-hidden",
                      index === 0 ? "rounded-t-lg" : index === 1 ? "rounded-bl-lg" : "rounded-br-lg",
                    )}
                  >
                    <video className="object-cover w-full h-full" muted loop autoPlay playsInline>
                      <source src={media.url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                ) : (
                  <Image
                    className={cn("w-full aspect-4-3", index === 0 ? "rounded-t-lg" : index === 1 ? "rounded-bl-lg" : "rounded-br-lg")}
                    src={media.url}
                    alt={media.title}
                    width={media.width}
                    height={media.height}
                  />
                )}

                {entry.media && entry.media?.length > 4 && index === 2 && (
                  <>
                    {/* <div className="img-overlay-2 absolute top-0 left-0 h-full w-full"></div> */}

                    <Button
                      size={ButtonSize.SMALL}
                      customClass="!font-secondary btn-glass absolute translate-y-[-50%] translate-x-[-50%] top-[50%] left-[50%] w-max"
                      theme={ButtonTheme.WHITE}
                      type="button"
                      variant={ButtonVariant.PRIMARY}
                      onClick={() => setModalOpen(true)}
                    >
                      <span className="flex items-center gap-xs normal-case text-white text-s">
                        <Icon name="image" size="20" className="stroke-white" />
                        {t("button.label.allimages")}
                      </span>
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* <h1>Testt</h1> */}
      {entry.media && <GalleryModal medias={entry.media} modalOpen={modalOpen} closeModal={() => setModalOpen(false)} />}
      {structuredData && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />}
    </div>
  );
};

export default UnitGallery;
